import type { QueuedOp } from './ops';

/**
 * Lot AW « Hors connexion » : ce que le téléphone garde (IndexedDB, base `cockpit-offline`).
 * - `responses` : la dernière réponse réussie des lectures autorisées (À faire, Projets, Agenda, fiches R1 déjà
 *   ouvertes, membres…), avec son heure ;
 * - `ops` : les changements faits hors connexion, dans l'ordre, jusqu'à leur envoi ;
 * - `blobs` : les photos des retours faits hors connexion ;
 * - `meta` : à qui appartiennent ces données (effacées à la déconnexion et au changement de personne).
 * Tout accès peut échouer (navigation privée, stockage plein) : l'appli marche alors comme avant, sans rien garder.
 */

export interface CachedResponse {
  key: string;
  path: string;
  data: unknown;
  /** Heure de la réponse (ISO). */
  savedAt: string;
}

export interface OfflineStorage {
  getResponse(key: string): Promise<CachedResponse | undefined>;
  putResponse(entry: CachedResponse): Promise<void>;
  listOps(): Promise<QueuedOp[]>;
  putOp(op: QueuedOp): Promise<void>;
  deleteOp(id: string): Promise<void>;
  putBlob(id: string, blob: Blob): Promise<void>;
  getBlob(id: string): Promise<Blob | undefined>;
  deleteBlob(id: string): Promise<void>;
  getMeta<T>(key: string): Promise<T | undefined>;
  setMeta(key: string, value: unknown): Promise<void>;
  /** Efface tout, sauf les réponses dont la clé est dans `keep` (configuration de connexion, non personnelle). */
  clear(keep?: readonly string[]): Promise<void>;
}

/** Stockage en mémoire (tests, ou navigateur sans IndexedDB). */
export function memoryStorage(): OfflineStorage {
  const responses = new Map<string, CachedResponse>();
  const ops = new Map<string, QueuedOp>();
  const blobs = new Map<string, Blob>();
  const meta = new Map<string, unknown>();
  const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
  return {
    getResponse: async (key) => {
      const entry = responses.get(key);
      return entry ? clone(entry) : undefined;
    },
    putResponse: async (entry) => {
      responses.set(entry.key, clone(entry));
    },
    listOps: async () => [...ops.values()].map(clone).sort((a, b) => a.seq - b.seq),
    putOp: async (op) => {
      ops.set(op.id, clone(op));
    },
    deleteOp: async (id) => {
      ops.delete(id);
    },
    putBlob: async (id, blob) => {
      blobs.set(id, blob);
    },
    getBlob: async (id) => blobs.get(id),
    deleteBlob: async (id) => {
      blobs.delete(id);
    },
    getMeta: async <T>(key: string) => meta.get(key) as T | undefined,
    setMeta: async (key, value) => {
      meta.set(key, value);
    },
    clear: async (keep = []) => {
      for (const key of [...responses.keys()]) if (!keep.includes(key)) responses.delete(key);
      ops.clear();
      blobs.clear();
      meta.clear();
    },
  };
}

const STORES = ['responses', 'ops', 'blobs', 'meta'] as const;
type StoreName = (typeof STORES)[number];

function plain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error);
    tx.onerror = () => reject(tx.error);
  });
}

/** IndexedDB ; null si le navigateur ne l'a pas. */
export function idbStorage(name = 'cockpit-offline'): OfflineStorage | null {
  if (typeof indexedDB === 'undefined') return null;
  let opening: Promise<IDBDatabase> | null = null;
  function open(): Promise<IDBDatabase> {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(name, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('responses')) db.createObjectStore('responses', { keyPath: 'key' });
        if (!db.objectStoreNames.contains('ops')) db.createObjectStore('ops', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('IndexedDB bloquée'));
    }).catch((err: unknown) => {
      opening = null;
      throw err;
    });
    return opening;
  }

  async function read<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
    try {
      const db = await open();
      return (await promisify(db.transaction(store).objectStore(store).get(key))) as T | undefined;
    } catch {
      return undefined;
    }
  }
  async function write(store: StoreName, work: (s: IDBObjectStore) => void): Promise<void> {
    try {
      const db = await open();
      const tx = db.transaction(store, 'readwrite');
      work(tx.objectStore(store));
      await done(tx);
    } catch {
      // Stockage indisponible ou plein : rien n'est gardé, l'appli continue.
    }
  }

  return {
    getResponse: (key) => read<CachedResponse>('responses', key),
    // Copie JSON : un objet réactif de Vue (Proxy) ne se range pas tel quel dans IndexedDB.
    putResponse: (entry) => write('responses', (s) => s.put(plain(entry))),
    listOps: async () => {
      try {
        const db = await open();
        const all = (await promisify(db.transaction('ops').objectStore('ops').getAll())) as QueuedOp[];
        return all.sort((a, b) => a.seq - b.seq);
      } catch {
        return [];
      }
    },
    putOp: (op) => write('ops', (s) => s.put(plain(op))),
    deleteOp: (id) => write('ops', (s) => s.delete(id)),
    putBlob: (id, blob) => write('blobs', (s) => s.put(blob, id)),
    getBlob: (id) => read<Blob>('blobs', id),
    deleteBlob: (id) => write('blobs', (s) => s.delete(id)),
    getMeta: <T>(key: string) => read<T>('meta', key),
    setMeta: (key, value) => write('meta', (s) => s.put(value, key)),
    clear: async (keep = []) => {
      try {
        const db = await open();
        const kept: CachedResponse[] = [];
        for (const key of keep) {
          const entry = (await promisify(db.transaction('responses').objectStore('responses').get(key))) as
            CachedResponse | undefined;
          if (entry) kept.push(entry);
        }
        const tx = db.transaction([...STORES], 'readwrite');
        for (const store of STORES) tx.objectStore(store).clear();
        for (const entry of kept) tx.objectStore('responses').put(entry);
        await done(tx);
      } catch {
        // Base illisible : on tente de la supprimer entièrement.
        try {
          indexedDB.deleteDatabase(name);
        } catch {
          // rien à faire
        }
      }
    },
  };
}
